import { FormikProps } from "formik";
import { FGCustomPanel, FormGenerator, IFormGeneratorProps, Loading } from "nsitools-react";
import * as React from "react";
import { useHistory, useLocation } from "react-router";
import styled from "styled-components";

import { useAuth } from "../contexts";
import { CancelButton, DeleteButton, EditButton, SaveButton } from "./applicationButtons";
import { ButtonsBloc, InlineButtonContainer } from "./applicationButtons/ButtonContainerStyles";
import { FGRouteDirtyChecker } from "./formGenerator/FGRouteDirtyChecker";
import { Classes } from "@blueprintjs/core";
import { useTl } from "../hooks";

export interface ISmallFormGeneratorProps<T> extends Omit<IFormGeneratorProps<T>, "children"> {
  loading?: boolean;
  onCancel?: () => void;
  onDelete?: (formik?: FormikProps<any>) => void;
  deleting?: boolean;
  saving?: boolean;
  showDeleteButton?: boolean;
  showSaveEmptyButtonLabel?: string;
  editMode?: boolean;
  hideButtons?: boolean;
  cancelEdit?: boolean;
  enableDirtyCheck?: boolean;
  editable?: boolean;
  canCheckRights?: boolean;
  dirtyCheckSaveButton?: boolean;
  validateOnChange?: boolean;
  drupal?: boolean;
  preventDefaultsaveOnEnter?: boolean;
}

const StyledForm = styled.div`
  & .${Classes.FORM_GROUP} {
    margin: 0 0 5px !important;
  }

  & .${Classes.CONTROL} {
    margin-bottom: 0 !important;
  }

  & .${Classes.LABEL} {
    max-width: 50px;
  }
  & .${Classes.FORM_CONTENT} {
    align-self: center;
  }
`;

export function SmallFormGenerator<T>(props: React.PropsWithChildren<ISmallFormGeneratorProps<T>>) {
  const history = useHistory();
  const {
    editable = true,
    children,
    loading = false,
    showDeleteButton = false,
    deleting,
    onDelete,
    onCancel,
    saving,
    editMode,
    minLabelWidth = 180,
    labelAlignment = "right",
    fill = true,
    showColons = true,
    inline = true,
    boldLabel = true,
    hideButtons = false,
    enableDirtyCheck = true,
    cancelEdit = false,
    canCheckRights = true,
    dirtyCheckSaveButton = true,
    drupal = false,
    preventDefaultsaveOnEnter = true,
    validateOnChange = false,
    showSaveEmptyButtonLabel,
    ...otherProps
  } = props;
  const { pathname } = useLocation();
  const { getCurrentPermission } = useAuth();
  const { tUnsafe } = useTl();
  const changeEditMode = React.useCallback(
    (newEditMode: boolean) => {
      if (!drupal) {
        history.push(pathname.replace("view", "edit"));
      } else {
        window.localStorage.setItem(
          "parameters",
          window.localStorage.getItem("parameters").replace(`"editMode":false`, `"editMode":true`)
        );
        window.location.reload();
      }
    },
    [drupal, history, pathname]
  );

  const readOnly = React.useMemo(
    () => canCheckRights && getCurrentPermission()?.permission === "R",
    [canCheckRights, getCurrentPermission]
  );
  const canDelete = React.useMemo(
    () => canCheckRights && getCurrentPermission()?.permission === "RWD",
    [canCheckRights, getCurrentPermission]
  );
  return (
    <StyledForm>
      <Loading isLoading={loading}>
        <FormGenerator
          editMode={editMode && !readOnly}
          minLabelWidth={minLabelWidth}
          labelAlignment={labelAlignment}
          fill={fill}
          inline={inline}
          boldLabel={boldLabel}
          showColons={showColons}
          preventDefaultSaveOnEnter={preventDefaultsaveOnEnter}
          formikConfig={{ validateOnChange: validateOnChange, validateOnBlur: true }}
          translateFn={tUnsafe}
          {...otherProps}
        >
          {enableDirtyCheck && <FGRouteDirtyChecker />}
          {children}
          {!hideButtons && (
            <FGCustomPanel>
              {(ctx) => (
                <InlineButtonContainer>
                  <ButtonsBloc>
                    {showDeleteButton && onDelete && editMode && canDelete && (
                      <DeleteButton
                        secured={!drupal}
                        minimal={false}
                        loading={deleting}
                        onDelete={() => onDelete(ctx.formik)}
                      />
                    )}
                  </ButtonsBloc>
                  <ButtonsBloc>
                    {!onCancel && cancelEdit && editMode && (
                      <CancelButton minimal={false} onClick={() => changeEditMode(false)} />
                    )}
                    {onCancel && <CancelButton minimal={false} onClick={onCancel} />}
                    {editMode && !readOnly ? (
                      !ctx.formik.dirty && showSaveEmptyButtonLabel ? (
                        <SaveButton minimal={false} loading={saving} text={showSaveEmptyButtonLabel} />
                      ) : (
                        <SaveButton
                          minimal={false}
                          loading={saving}
                          disabled={dirtyCheckSaveButton && !ctx.formik.dirty}
                        />
                      )
                    ) : (
                      editable &&
                      !readOnly && <EditButton secured={!drupal} minimal={false} onClick={() => changeEditMode(true)} />
                    )}
                  </ButtonsBloc>
                </InlineButtonContainer>
              )}
            </FGCustomPanel>
          )}
        </FormGenerator>
      </Loading>
    </StyledForm>
  );
}
